import { useState } from 'react';
import { useAddEmployeeMutation } from 'redux/features/users/authApi';
import { usersApi } from 'redux/features/users/userApi';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { copyToClipboard } from 'common/commonFunctions';
import { useGetDepartmentsQuery } from 'redux/features/departmentApi';
import { useGetDesignationsByDepartmentQuery } from 'redux/features/designationApi';

import Icon from '../../../components/AppIcon';

const AddEmployeeModal = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    first_name: '',
    middle_name: '',
    last_name: '',
    email: '',
    role: 'EMPLOYEE',
    department_id: '',
    designation_id: '',
  });
  const [selectedDepartmentId, setSelectedDepartmentId] = useState(null);

  const { data: departmentsData } = useGetDepartmentsQuery();
  const { data: designationsData, isFetching: designationsLoading } =
    useGetDesignationsByDepartmentQuery(selectedDepartmentId, {
      skip: !selectedDepartmentId,
    });
  const [showSuccess, setShowSuccess] = useState(false);
  const [createdEmployee, setCreatedEmployee] = useState({
    employeeId: '',
    password: '',
  });
  const [addEmployee, { isLoading }] = useAddEmployeeMutation();

  const handleInputChange = (e) => {
    const { name, value } = e?.target || {};
    setErrors((prev) => ({
      ...prev,
      [name]: '',
    }));
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === 'department_id') {
      setSelectedDepartmentId(value);
      setFormData((prev) => ({
        ...prev,
        designation_id: '',
      }));
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    const newErrors = {};
    if (!formData?.first_name?.trim()) newErrors.first_name = 'First Name is required';
    if (!formData?.middle_name?.trim()) newErrors.middle_name = 'Middle Name is required';
    if (!formData?.last_name?.trim()) newErrors.last_name = 'Last Name is required';
    if (!formData?.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!formData?.email?.includes('@')) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!formData?.department_id) newErrors.department_id = 'Department is required';
    if (!formData?.designation_id) newErrors.designation_id = 'Designation is required';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    try {
      const newEmployee = await addEmployee(formData);
      if (newEmployee?.error) {
        if (newEmployee.error?.data) {
          newEmployee?.error?.data?.errors?.map((error) => {
            setErrors((prev) => ({
              ...prev,
              [error?.path]: error?.msg,
            }));
          });
        }
        toast.error(newEmployee?.error?.data?.message);
      } else {
        setCreatedEmployee({
          employeeId: newEmployee?.data?.employee_id,
          password: newEmployee?.data?.password,
        });
        dispatch(usersApi.util.invalidateTags(['Users']));
        setShowSuccess(true);
      }
    } catch (error) {
      console.log('Error while adding new employee', error);
    }
  };

  const handleClose = () => {
    setFormData({
      first_name: '',
      middle_name: '',
      last_name: '',
      email: '',
      role: 'EMPLOYEE',
    });
    setShowSuccess(false);
    setCreatedEmployee(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
        {!showSuccess ? (
          <>
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-2">
                <Icon name="UserPlus" size={24} color="var(--color-primary)" />
                <h3 className="text-xl font-semibold text-gray-900">Add New Employee</h3>
              </div>
              <button onClick={handleClose} className="text-gray-400 hover:text-gray-600">
                <Icon name="X" size={20} />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* First Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    First Name *
                  </label>
                  <input
                    type="text"
                    name="first_name"
                    value={formData?.first_name}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                      errors?.first_name ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter first name"
                  />
                  {errors?.first_name && (
                    <p className="mt-1 text-xs text-red-600">{errors?.first_name}</p>
                  )}
                </div>

                {/* Middle Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Middle Name *
                  </label>
                  <input
                    type="text"
                    name="middle_name"
                    value={formData?.middle_name}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                      errors?.middle_name ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter middle name"
                  />
                  {errors?.middle_name && (
                    <p className="mt-1 text-xs text-red-600">{errors?.middle_name}</p>
                  )}
                </div>

                {/* Last Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    name="last_name"
                    value={formData?.last_name}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                      errors?.last_name ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter last name"
                  />
                  {errors?.last_name && (
                    <p className="mt-1 text-xs text-red-600">{errors?.last_name}</p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData?.email}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                      errors?.email ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter email address"
                  />
                  {errors?.email && <p className="mt-1 text-xs text-red-600">{errors?.email}</p>}
                </div>

                {/* Department */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Department *
                  </label>
                  <select
                    name="department_id"
                    value={formData?.department_id}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                      errors?.department_id ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Select Department</option>
                    {departmentsData?.departments?.map((dept) => (
                      <option key={dept?.id} value={dept?.id}>
                        {dept?.name}
                      </option>
                    ))}
                  </select>
                  {errors?.department_id && (
                    <p className="mt-1 text-xs text-red-600">{errors?.department_id}</p>
                  )}
                </div>

                {/* Designation */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Designation *
                  </label>
                  <select
                    name="designation_id"
                    value={formData?.designation_id}
                    onChange={handleInputChange}
                    disabled={!formData?.department_id || designationsLoading}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                      errors?.designation_id ? 'border-red-500' : 'border-gray-300'
                    } ${!formData?.department_id ? 'bg-gray-50' : 'bg-white'}`}
                  >
                    <option value="">
                      {designationsLoading
                        ? 'Loading...'
                        : formData?.department_id
                          ? 'Select Designation'
                          : 'Select Department First'}
                    </option>
                    {designationsData?.map((des) => (
                      <option key={des?.id} value={des?.id}>
                        {des?.name}
                      </option>
                    ))}
                  </select>
                  {errors?.designation_id && (
                    <p className="mt-1 text-xs text-red-600">{errors?.designation_id}</p>
                  )}
                </div>

                {/* Role */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Role *</label>
                  <select
                    name="role"
                    value={formData?.role}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="EMPLOYEE">Employee</option>
                    <option value="ADMIN">Admin</option>
                  </select>
                </div>
              </div>

              <div className="bg-blue-50 p-3 rounded-md flex items-start space-x-2">
                <Icon name="Info" size={18} className="text-blue-600 mt-0.5" />
                <p className="text-sm text-blue-700">
                  Employee ID and password will be auto-generated upon creation.
                </p>
              </div>

              {/* Buttons */}
              <div className="flex justify-end space-x-3 pt-2">
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="px-6 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center min-w-[120px]"
                >
                  {isLoading ? (
                    <Icon name="Loader2" size={20} className="animate-spin" />
                  ) : (
                    'Add Employee'
                  )}
                </button>
              </div>
            </form>
          </>
        ) : (
          <>
            {/* Success Screen */}
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                <Icon name="CheckCircle" size={24} color="#10B981" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Employee Added Successfully!
              </h3>
              <p className="text-sm text-gray-500 mb-6">
                Please share these credentials with the new employee:
              </p>

              <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-700">Employee ID:</span>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-900 font-mono">
                      {createdEmployee?.employeeId}
                    </span>
                    <button
                      onClick={() => copyToClipboard(createdEmployee?.employeeId)}
                      className="text-gray-400 hover:text-gray-600"
                      title="Copy to clipboard"
                    >
                      <Icon name="Copy" size={16} />
                    </button>
                  </div>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-700">Password:</span>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-900 font-mono">
                      {createdEmployee?.password}
                    </span>
                    <button
                      onClick={() => copyToClipboard(createdEmployee?.password)}
                      className="text-gray-400 hover:text-gray-600"
                      title="Copy to clipboard"
                    >
                      <Icon name="Copy" size={16} />
                    </button>
                  </div>
                </div>
              </div>

              <div className="bg-yellow-50 p-3 rounded-md mt-4">
                <p className="text-sm text-yellow-800">
                  <Icon name="AlertTriangle" size={16} className="inline mr-1" />
                  Make sure to save these credentials securely!
                </p>
              </div>

              <button
                onClick={handleClose}
                className="w-full mt-6 px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                Done
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AddEmployeeModal;
